import { MenuItem } from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import React from "react";

import { ID } from "common/entities/IEntity";
import { BlockVariant, BlockView } from "common/views/block/BlockView.view";
import { BoxVariant, BoxView } from "common/views/box/Box.view";
import { ButtonVariant, ButtonView } from "common/views/button/Button.view";
import { DropdownSelectVariant, ofType } from "common/views/dropdownSelect/DropdownSelect.view";
import HeadingView, { HeadingVariant } from "common/views/heading/Heading.view";
import IconView from "common/views/icon/Icon.view";
import { RowsView } from "common/views/rows/Rows.view";
import Label from "core/components/label/Label";
import { isEmpty } from "core/util/ArrayUtils";
import { TagSetsComponentConfig, ViewType } from "./TagSetsComponentConfig";


interface ITag {
	id: ID;
	name: string;
}

interface ITagSet {
	id: ID;
	name: string;
	tags: ITag[];
}

interface ITagSetsComponentProps {
	config?: TagSetsComponentConfig;
	data: ITagSet[];
	onChange?: (ids: ID[]) => void;
	value?: ID[];
}

const TagsDropdownSelect = ofType<ITag>();

interface ITagSetsComponentState {
	expanded: { [key: string]: boolean; };
}

export class TagSetsComponent extends React.Component<ITagSetsComponentProps, ITagSetsComponentState> {

	public state = { expanded: { } };

	public render() {
		const { config, data, value } = this.props;

		return [
			config.showSelected && !isEmpty(value) && (
				<BlockView key="selected" variant={BlockVariant.REFINE} viewKey="selected">
					<HeadingView variant={HeadingVariant.H4} viewKey="selected.title" children={<Label k="tags.selected" v="Selected" />} />
					{this.renderSelectedTags()}
				</BlockView>
			),
			<RowsView key="tagsets" viewKey="rows">
				{data.map(this.renderTagSet)}
			</RowsView>
		];
	}

	private renderSelectedTags = () => {
		const { data, value } = this.props;
		if (isEmpty(data) || isEmpty(value)) {
			return false;
		}
		const tags = data.reduce<any>((acc, ts) => {
			if (ts.tags) {
				ts.tags.forEach((tag) => acc[tag.id] = tag);
			}
			return acc;
		}, {});
		return value.map((tagId) => {
			const tag = tags[tagId];
			if (!tag) {
				return null;
			}
			return (
				<ButtonView key={tagId} onClick={this.removeTag(tagId)} variant={ButtonVariant.TAG} viewKey="selected-tag">
					<IconView icon="cross" viewKey="selected-tag-icon">
						<Label k={`tag.${tagId}`} v={tag.name} />
					</IconView>
				</ButtonView>
			);
		});
	}

	private renderTagSet = (tagSet: ITagSet) => {
		const { config } = this.props;

		const tsLabel = <Label key="name" k={`tagSet.${tagSet.name}`} v={tagSet.name} />;

		const selectedCount = this.getSelectedCount(tagSet.tags.map((tag) => tag.id));

		switch(config.viewType) {
			case ViewType.DROPDOWNS:
				return (
					<TagsDropdownSelect
						key={tagSet.id}
						items={tagSet.tags}
						itemRenderer={(tag, { handleSelect }) => (
							<MenuItem
								key={tag.id}
								icon={this.isTagSelected(tag.id) ? IconNames.SMALL_TICK : undefined}
								onClick={handleSelect}
								text={<Label k={`tag.${tag.name}`} v={tag.name} />}
							/>
						)}
						label={tsLabel}
						onChange={(tag) => this.selectTag(tag.id, tagSet)()}
						variant={DropdownSelectVariant.SECONDARY}
						viewKey="tagset-dropdown"
					>
						{tsLabel}
						{selectedCount > 0 && ' (' + selectedCount + ')'}
					</TagsDropdownSelect>
				);
			default: // box view
				return (
					<BoxView
						key={tagSet.id}
						title={tsLabel}
						variant={BoxVariant.MINIMAL}
						viewKey="tagset-box"
					>
						{tagSet.tags.map((tag) => (
							<ButtonView key={tag.id}
								isActive={this.isTagSelected(tag.id)}
								onClick={this.selectTag(tag.id, tagSet)}
								style={{display: 'block'}}
								variant={ButtonVariant.MENU_BUTTON}
								viewKey="tag-button"
							>
								<Label k={`tag.${tag.name}`} v={tag.name} />
							</ButtonView>
						))}
					</BoxView>
				);
		}
	}

	private isTagSelected = (id: ID): boolean => {
		return !isEmpty(this.props.value) && this.props.value.includes(id);
	}

	private getSelectedCount(tagIds: ID[]): number {
		return isEmpty(this.props.value) ? 0 : tagIds.reduce(
			(acc, tid) => this.props.value.includes(tid) ? acc + 1 : acc, 0
		);
	}

	private removeTag = (tagId: ID) => () => {
		this.props.onChange(this.props.value ? this.props.value.filter((v) => v !== tagId) : undefined);
	}

	private selectTag = (tagId: ID, tagSet: ITagSet) => () => {
		const { config, onChange, value } = this.props;

		if (isEmpty(value)) {
			onChange([tagId]);
		} else if (value.length === 1 && value[0] === tagId) {
			onChange(undefined);
		} else {
			if (!config.multiSelectTags) {
				onChange([tagId]);
				return;
			}

			if (config.multiSelectTagsets) {
				onChange(value.includes(tagId) ? value.filter((v) => v !== tagId) : [...value, tagId]);
			} else {
				const setTagIds = tagSet.tags.map((t) => t.id);
				let wasSelected = false;
				const nextValue = value.filter((v) => {
					if (v === tagId) {
						wasSelected = true;
						return false;
					}
					return !setTagIds.includes(v);
				});
				if (!wasSelected) {
					nextValue.push(tagId);
				}
				onChange(nextValue);
			}
		}
	}
}



// WEBPACK FOOTER //
// ./src/panels/refineTag/components/TagSetsComponent.tsx